import { defaultButtonTheme } from "./components/defaultButtonTheme";
import { defaultRippleTheme } from "./components/defaultRippleTheme";
import { TombacTheme } from "../shared/tombac/tombacTypes";
import { defaultToggleGroupTheme } from "./components/defaultToggleGroupTheme";

// 5% steps were used to obtain darker/lighter shades
export const defaultColorPalette = {
  lightRed: "hsl(1, 79%, 59%)",
  brandRed: "hsl(3, 85%, 47%)",
  darkRed: "hsl(1, 85%, 37%)",

  lightGreen: "hsl(154, 100%, 43%)",
  brandGreen: "hsl(154, 100%, 33%)",
  darkGreen: "hsl(154, 100%, 28%)",

  lightIndigo: "hsl(205, 100%, 35%)",
  brandIndigo: "hsl(205, 100%, 25%)",
  darkIndigo: "hsl(205, 100%, 25%)",

  lightBlue: "hsl(200, 100%, 60%)",
  brandBlue: "hsl(200, 100%, 50%)",
  darkBlue: "hsl(200, 100%, 45%)",

  lightOrange: "hsl(22, 100%, 60%)",
  brandOrange: "hsl(22, 100%, 50%)",
  darkOrange: "hsl(33, 79%, 45%)",

  lightYellow: "hsl(44, 97%, 69%)",
  brandYellow: "hsl(44, 98%, 59%)",
  darkYellow: "hsl(44, 98%, 54%)",
};

// 8% or 9% steps, depending on color
export const defaultTheme: TombacTheme = {
  baseSpace: 8,
  baseUnit: "rem",
  colors: {
    accent: {
      100: "hsl(201, 100%, 97%)",
      200: "hsl(200, 100%, 93%)",
      300: "hsl(200, 100%, 82%)",
      400: "hsl(200, 100%, 68%)",
      500: "hsl(200, 100%, 50%)",
      600: "hsl(200, 100%, 42%)",
      700: "hsl(204, 67%, 40%)",
      800: "hsl(204, 67%, 30%)",
      900: "hsl(204, 67%, 20%)",
      alpha: {
        8: "hsla(204, 67%, 63%, 0.08)",
        16: "hsla(204, 67%, 63%, 0.16)",
        24: "hsla(204, 67%, 63%, 0.24)",
        32: "hsla(204, 67%, 63%, 0.32)",
        40: "hsla(204, 67%, 63%, 0.4)",
        48: "hsla(204, 67%, 63%, 0.48)",
      },
    },
    alert: {
      100: "hsl(43, 100%, 94%)",
      200: "hsl(46, 100%, 89%)",
      300: "hsl(44, 100%, 81%)",
      400: "hsl(44, 92%, 70%)",
      500: "hsl(44, 98%, 59%)",
      600: "hsl(40, 95%, 56%)",
      700: "hsl(40, 100%, 42%)",
      800: "hsl(40, 100%, 31%)",
      900: "hsl(40, 96%, 26%)",
      alpha: {
        8: "hsla(44, 98%, 59%, 0.08)",
        16: "hsla(44, 98%, 59%, 0.16)",
        24: "hsla(44, 98%, 59%, 0.24)",
        32: "hsla(44, 98%, 59%, 0.32)",
        40: "hsla(44, 98%, 59%, 0.4)",
        48: "hsla(44, 98%, 59%, 0.48)",
      },
    },
    neutral: {
      100: "hsl(0, 0%, 100%)",
      200: "hsl(0, 0%, 98%)",
      300: "hsl(0, 0%, 95%)",
      400: "hsl(0, 0%, 90%)",
      500: "hsl(0, 0%, 80%)",
      600: "hsl(0, 0%, 63%)",
      700: "hsl(0, 0%, 40%)",
      800: "hsl(0, 0%, 20%)",
      900: "hsl(0, 0%, 0%)",
      alpha: {
        8: "hsla(0, 0%, 0%, 0.08)",
        16: "hsla(0, 0%, 0%, 0.16)",
        24: "hsla(0, 0%, 0%, 0.24)",
        32: "hsla(0, 0%, 0%, 0.32)",
        40: "hsla(0, 0%, 0%, 0.4)",
        48: "hsla(0, 0%, 0%, 0.48)",
      },
      invertedAlpha: {
        8: "hsla(0, 0%, 100%, 0.08)",
        16: "hsla(0, 0%, 100%, 0.16)",
        24: "hsla(0, 0%, 100%, 0.24)",
        32: "hsla(0, 0%, 100%, 0.32)",
        40: "hsla(0, 0%, 100%, 0.4)",
        48: "hsla(0, 0%, 100%, 0.48)",
      },
    },
    primary: {
      100: "hsl(2, 100%, 95%)",
      200: "hsl(1, 100%, 85%)",
      300: "hsl(1, 100%, 73%)",
      400: "hsl(1, 79%, 59%)",
      500: "hsl(3, 85%, 47%)",
      600: "hsl(1, 85%, 41%)",
      700: "hsl(1, 83%, 35%)",
      800: "hsl(1, 99%, 25%)",
      900: "hsl(1, 99%, 14%)",
      alpha: {
        8: "hsla(3, 85%, 47%, 0.08)",
        16: "hsla(3, 85%, 47%, 0.16)",
        24: "hsla(3, 85%, 47%, 0.24)",
        32: "hsla(3, 85%, 47%, 0.32)",
        40: "hsla(3, 85%, 47%, 0.4)",
        48: "hsla(3, 85%, 47%, 0.48)",
      },
    },
    success: {
      100: "hsl(119, 100%, 97%)",
      200: "hsl(126, 95%, 91%)",
      300: "hsl(126, 80%, 81%)",
      400: "hsl(133, 52%, 65%)",
      500: "hsl(154, 100%, 33%)",
      600: "hsl(154, 100%, 25%)",
      700: "hsl(154, 100%, 16%)",
      800: "hsl(153, 97%, 12%)",
      900: "hsl(153, 97%, 12%)",
      alpha: {
        8: "hsla(132, 64%, 66%, 0.08)",
        16: "hsla(132, 64%, 66%, 0.16)",
        24: "hsla(132, 64%, 66%, 0.24)",
        32: "hsla(132, 64%, 66%, 0.32)",
        40: "hsla(132, 64%, 66%, 0.4)",
        48: "hsla(132, 64%, 66%, 0.48)",
      },
    },
    danger: {
      100: "hsl(2, 100%, 95%)",
      200: "hsl(1, 100%, 85%)",
      300: "hsl(1, 100%, 73%)",
      400: "hsl(1, 79%, 59%)",
      500: "hsl(3, 85%, 47%)",
      600: "hsl(1, 85%, 41%)",
      700: "hsl(1, 83%, 35%)",
      800: "hsl(1, 99%, 25%)",
      900: "hsl(1, 99%, 14%)",
      alpha: {
        8: "hsla(3, 85%, 47%, 0.08)",
        16: "hsla(3, 85%, 47%, 0.16)",
        24: "hsla(3, 85%, 47%, 0.24)",
        32: "hsla(3, 85%, 47%, 0.32)",
        40: "hsla(3, 85%, 47%, 0.4)",
        48: "hsla(3, 85%, 47%, 0.48)",
      },
    },
  },
  components: {
    button: defaultButtonTheme,
    ripple: defaultRippleTheme,
    toggleGroup: defaultToggleGroupTheme,
  },
  typography: {
    altFontFamily: "Gilroy, sans-serif",
    defaultColor: ["neutral", 900],
    defaultFontSize: 14,
    defaultFontWeight: "400",
    defaultLineHeight: 1.5,
    defaultFontFamily: '"Proxima Nova", sans-serif',
  },
};
